import { generatePath, To } from 'react-router-dom'

import { FilterValue } from 'components/Filter'
import { Table, usePagination } from 'components/Table'
import { PaginationProps } from 'components/Table/Pagination'
import { useSort } from 'components/Table/Sort'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { calculatePagination } from 'providers/tanstack-query/utils'
import { ROUTES } from 'routes'
import { DEFAULT_SORT, SORTABLE_COLUMNS } from './constants'
import { useAdUnits } from './hooks/useAdUnits'
import { Alert, useGetAlerts } from './hooks/useGetAlerts'
import { QueryProps } from './types/api'
import { AdUnitForTable, AdUnitSortBy } from './types/model'
import { useColumns } from './useColumns'

export type TableProps = {
  filters: FilterValue[]
}

export const AdUnitsTable = ({ filters }: TableProps): JSX.Element => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const { page, rowsPerPage, setPage, ...usePaginationProps } = usePagination({
    resetOnChange: [filters],
  })
  const { props: sortProps, sort } = useSort<AdUnitForTable, AdUnitSortBy>(
    DEFAULT_SORT,
    SORTABLE_COLUMNS,
  )

  const queryVariables: QueryProps = {
    filters,
    ...calculatePagination({ page, rowsPerPage }),
    sort,
  }
  const { adUnits, count, loading, setAdUnits } = useAdUnits(queryVariables)
  const alerts: Alert[] = useGetAlerts(queryVariables)

  const columns = useColumns({ alerts, count, filters })

  const handleRowClick = (row: AdUnitForTable): To =>
    generatePath(ROUTES.INVENTORY.AD_UNIT.EDIT, {
      id: row.id,
      tab: 'general',
      workspaceId,
    })

  const paginationProps: PaginationProps = {
    count,
    page,
    rowsPerPage,
    setPage,
    ...usePaginationProps,
  }

  return (
    <Table<AdUnitForTable>
      columns={columns}
      data={adUnits}
      loading={loading}
      meta={{ stickyHeader: true }}
      onClickRow={ability.can('access', 'AdUnitPage-edit') ? id => handleRowClick(id) : undefined}
      onUpdateData={setAdUnits}
      pagination={paginationProps}
      sort={sortProps}
    />
  )
}
